<template>
	<div>
		<div class="header-section">
			<h1>Архив заявок</h1>
			<PrimeButton
				label="Составление заявки"
				icon="pi pi-plus"
				@click="showCreateModal = true"
			/>
		</div>

		<div v-if="loading" class="loading">
			Загрузка заявок...
		</div>

		<div v-else-if="requests.length === 0" class="empty-state">
			<p>Заявки не найдены. Создайте заявку, нажав кнопку "Составление заявки".</p>
		</div>

		<div v-else>
			<PrimeDataTable
				:value="requests"
				v-model:first="requestsTableFirst"
				stripedRows
				showGridlines
				size="small"
				:paginator="true"
				:rows="10"
				paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
				currentPageReportTemplate="{first} to {last} of {totalRecords}"
			>
				<PrimeColumn header="(#)" :style="{ width: '1%', whiteSpace: 'nowrap' }">
					<template #body="slotProps">
						{{ requestsTableFirst + slotProps.index + 1 }}
					</template>
				</PrimeColumn>
				<PrimeColumn field="supplierName" header="Поставщик" :sortable="true" />
				<PrimeColumn field="supplierEmail" header="Email" />
				<PrimeColumn field="status" header="Статус" :sortable="true">
					<template #body="slotProps">
						<span :class="{ 'status-pending': slotProps.data.status === 0, 'status-completed': slotProps.data.status === 1 }">
							{{ slotProps.data.status === 0 ? "В работе" : "Завершена" }}
						</span>
					</template>
				</PrimeColumn>
				<PrimeColumn field="createdDate" header="Дата создания" :sortable="true">
					<template #body="slotProps">
						{{ formatDate(slotProps.data.createdDate) }}
					</template>
				</PrimeColumn>
				<PrimeColumn field="items" header="Количество позиций">
					<template #body="slotProps">
						{{ slotProps.data.items ? slotProps.data.items.length : 0 }}
					</template>
				</PrimeColumn>
				<PrimeColumn header="Действия" :exportable="false" :style="{ width: '1%', whiteSpace: 'nowrap' }">
					<template #body="slotProps">
						<PrimeButton
							v-if="slotProps.data.status === 0"
							icon="pi pi-pencil"
							variant="outlined"
							size="small"
							@click="editRequest(slotProps.data.id)"
							title="Редактировать"
						/>
						<PrimeButton
							icon="pi pi-eye"
							variant="outlined"
							size="small"
							@click="viewRequest(slotProps.data)"
							title="Просмотр деталей"
						/>
						<PrimeButton
							icon="pi pi-download"
							variant="outlined"
							size="small"
							@click="exportRequest(slotProps.data.id)"
							title="Экспорт Excel"
						/>
						<PrimeButton
							icon="pi pi-trash"
							variant="outlined"
							severity="danger"
							size="small"
							@click="deleteRequest(slotProps.data.id)"
							title="Удалить"
						/>
					</template>
				</PrimeColumn>
			</PrimeDataTable>

			<PrimeDialog v-model:visible="viewDialog" :style="{ width: '600px' }" header="Детали заявки" :modal="true">
				<div v-if="selectedRequest">
					<div class="request-info">
						<p><strong>ID заявки:</strong> {{ selectedRequest.id }}</p>
						<p><strong>Поставщик:</strong> {{ selectedRequest.supplierName }}</p>
						<p><strong>Email:</strong> {{ selectedRequest.supplierEmail }}</p>
						<p><strong>Создано:</strong> {{ formatDate(selectedRequest.createdDate) }}</p>
					</div>
					<div class="request-items">
						<h4>Позиции:</h4>
						<table>
							<thead>
								<tr>
									<th>Наименование</th>
									<th>Марка</th>
									<th>Мера количества</th>
									<th>Количество</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(item, index) in selectedRequest.items" :key="index">
									<td>{{ item.spareComponentName }}</td>
									<td>{{ item.carBrand }}</td>
									<td>{{ item.quantifier }}</td>
									<td>{{ item.quantity }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<template #footer>
					<PrimeButton label="Закрыть" icon="pi pi-times" @click="viewDialog = false" />
					<PrimeButton
						v-if="selectedRequest"
						label="Экспорт Excel"
						icon="pi pi-download"
						@click="exportRequest(selectedRequest.id)"
					/>
				</template>
			</PrimeDialog>
		</div>

		<PrimeDialog v-model:visible="showCreateModal" :style="{ width: '400px' }" header="Создание заявки" :modal="true">
			<div class="create-request-form">
				<label>Выберите поставщика:</label>
				<PrimeDropdown
					v-model="selectedSupplierForCreate"
					:options="suppliers"
					optionLabel="name"
					placeholder="Выберите поставщика"
					class="supplier-dropdown"
				/>
			</div>
			<template #footer>
				<PrimeButton label="Отмена" icon="pi pi-times" @click="showCreateModal = false" severity="secondary" />
				<PrimeButton
					label="Создать"
					icon="pi pi-check"
					@click="createRequest"
					:disabled="!selectedSupplierForCreate"
				/>
			</template>
		</PrimeDialog>
	</div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { api } from "../api";
import { useToast } from "primevue/usetoast";

const router = useRouter();
const requests = ref([]);
const suppliers = ref([]);
const requestsTableFirst = ref(0);
const loading = ref(true);
const viewDialog = ref(false);
const showCreateModal = ref(false);
const selectedRequest = ref(null);
const selectedSupplierForCreate = ref(null);
const toast = useToast();

const load = async () => {
	loading.value = true;
	try {
		const [requestsResponse, suppliersResponse] = await Promise.all([
			api.get("/requests"),
			api.get("/suppliers")
		]);
		requests.value = requestsResponse.data;
		suppliers.value = suppliersResponse.data;
	} catch (err) {
		console.error(err);
		toast.add({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить данные", life: 3000 });
	} finally {
		loading.value = false;
	}
};

onMounted(load);

const formatDate = (dateString) => {
	if (!dateString) {
		return "N/A";
	}

	return new Date(dateString).toLocaleString();
};

const viewRequest = (request) => {
	selectedRequest.value = request;
	viewDialog.value = true;
};

const exportRequest = async (id) => {
	try {
		const response = await api.get(`/requests/${id}/export`, {
			responseType: "blob"
		});

		const blob = new Blob([response.data], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		});

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", `Request_${id}.xlsx`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
		toast.add({ severity: "success", summary: "Успешно", detail: "Файл Excel загружен", life: 3000 });
	} catch (err) {
		console.error(err);
		toast.add({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить файл Excel", life: 3000 });
	}
};

const createRequest = async () => {
	if (!selectedSupplierForCreate.value) {
		toast.add({ severity: "warn", summary: "Проверка", detail: "Пожалуйста, выберите поставщика", life: 3000 });
		return;
	}

	try {
		const response = await api.post("/requests", {
			supplierId: selectedSupplierForCreate.value.id,
			items: []
		});

		showCreateModal.value = false;
		selectedSupplierForCreate.value = null;
		router.push(`/request-builder/${response.data.id}`);
	} catch (err) {
		console.error(err);
		const errorMessage = err.response?.data?.message || err.response?.data || err.message || "Не удалось создать заявку";
		toast.add({ severity: "error", summary: "Ошибка", detail: errorMessage, life: 3000 });
	}
};

const editRequest = (id) => {
	router.push(`/request-builder/${id}`);
};

const deleteRequest = async (id) => {
	if (!window.confirm(`Удалить заявку поставщику #${id}?`)) {
		return;
	}

	try {
		await api.delete(`/requests/${id}`);
		requests.value = requests.value.filter((request) => request.id !== id);

		if (selectedRequest.value?.id === id) {
			selectedRequest.value = null;
			viewDialog.value = false;
		}

		toast.add({ severity: "success", summary: "Успешно", detail: "Заявка удалена", life: 3000 });
	} catch (err) {
		console.error(err);
		const errorMessage = err.response?.data?.message || err.response?.data || err.message || "Не удалось удалить заявку";
		toast.add({ severity: "error", summary: "Ошибка", detail: errorMessage, life: 3000 });
	}
};
</script>

<style scoped>
.loading {
	background: white;
	padding: 3rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	text-align: center;
	color: #666;
}

.empty-state {
	background: white;
	padding: 3rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	text-align: center;
	color: #666;
}

.request-info {
	margin-bottom: 1.5rem;
	padding-bottom: 1.5rem;
	border-bottom: 1px solid #eee;
}

.request-info p {
	margin: 0.5rem 0;
}

.request-items h4 {
	margin-bottom: 1rem;
	color: #2c3e50;
}

.request-items table {
	width: 100%;
	border-collapse: collapse;
	margin-top: 0.5rem;
}

.request-items table th {
	background-color: #f8f9fa;
	padding: 0.75rem;
	text-align: left;
	border-bottom: 2px solid #dee2e6;
	font-weight: 600;
}

.request-items table td {
	padding: 0.5rem;
	border-bottom: 1px solid #dee2e6;
}

.header-section {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1.5rem;
}

.create-request-form {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.create-request-form label {
	font-weight: 600;
	color: #2c3e50;
}

.supplier-dropdown {
	width: 100%;
}

.status-pending {
	color: #f59e0b;
	font-weight: 600;
}

.status-completed {
	color: #10b981;
	font-weight: 600;
}
</style>
